/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useMutation } from '@apollo/client';
import IconComponent from 'components/common/IconComponent';
import Spinner from 'components/Spinner';
import { ERRORS } from 'constants/notify';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { AuthAction } from 'redux/actions';
import ROUTES from 'routes/routeConstants';
import { UPDATE_USER } from 'services/graphql/mutations/users';
import { GET_COMPANY_LOCATION } from 'services/graphql/queries/location.query';
import { ILocation } from 'types/location';
import { CompanyRoleEnum, RoleEnum } from 'types/user';
import { defaultNotification, formatAddress, getDefaultAvatarUrl } from 'utils';

import useInfiniteDataQuery from './useInfiniteDataQuery';

const { userSignOut } = AuthAction;

type ModalState = 'popover' | 'upload-avatar' | 'reset-password' | 'location';

const useUserMenu = (showLocationOption?: boolean) => {
  const [modalVisibleState, setModalVisibleState] = useState<ModalState | undefined>(undefined);
  const { id, role, companyRole, mainLocation, ...user } = useCurrentLoggedInUser(); // test nha
  const isUserClient = role === RoleEnum.CLIENT;
  const isClientAdmin = isUserClient && companyRole === CompanyRoleEnum.ADMIN;
  const [updateUser, { loading: updateUserLoading }] = useMutation(UPDATE_USER);
  const { data: allLocations } = useInfiniteDataQuery<ILocation>({
    graphQLQuery: GET_COMPANY_LOCATION,
    queryProps: { skip: !isClientAdmin },
  });

  const locations = isClientAdmin ? allLocations : user?.usersLocations?.map((uL) => uL.location) || [];
  const dispatch = useDispatch();
  const history = useHistory();
  const { fullName, smallAvatarURL, avatarURL } = useMemo(() => {
    const lastName = user?.lastName || '';
    const firstName = user?.firstName || '';
    const defaultAvatar = getDefaultAvatarUrl(`${firstName} ${lastName}`);
    const tempAvatarURL = user?.avatar?.pipeFrom || defaultAvatar;
    const tempSmallAvatarURL = user?.avatar?.thumbnailPipeFrom || user?.avatar?.pipeFrom || defaultAvatar;

    return {
      avatarURL: tempAvatarURL,
      smallAvatarURL: tempSmallAvatarURL,
      fullName: `${firstName} ${lastName}`,
    };
  }, [user]);

  const redirect = useCallback((url) => {
    setModalVisibleState(undefined);
    history.push(url);
  }, []);

  const onUserClick = useCallback(() => {
    setModalVisibleState((c) => {
      if (c) return undefined;
      return 'popover';
    });
  }, []);

  const onAvatarClick = useCallback((e) => {
    e.stopPropagation();
    setModalVisibleState('upload-avatar');
  }, []);

  const closeUploadModal = useCallback(() => {
    setModalVisibleState(undefined);
  }, []);

  const onChangePasswordCancel = useCallback(() => {
    setModalVisibleState(undefined);
  }, []);

  const onChangePasswordClick = useCallback(() => {
    setModalVisibleState('reset-password');
  }, []);

  const onLocationModalClose = useCallback(() => {
    setModalVisibleState(undefined);
  }, []);

  const onLocationClick = useCallback(() => {
    setModalVisibleState('location');
  }, []);

  const onMainLocationSelect = async (location: ILocation) => {
    if (mainLocation?.id === location.id) return;
    try {
      await updateUser({
        variables: {
          filter: {
            id,
          },
          updatedDoc: {
            mainLocation: location.id,
          },
        },
      });
      defaultNotification({ success: 'Update current Office Location successfully!' });
      setModalVisibleState(undefined);
    } catch (err) {
      defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
    }
  };

  const userMenuOptions = (
    <ul className='gx-user-popover'>
      {showLocationOption && isUserClient && (
        <li>
          <button className='gx-user-profile-btn' type='button' onClick={onLocationClick}>
            {mainLocation ? `Main Location: ${mainLocation?.name}` : 'Set Main Location'}
          </button>
        </li>
      )}
      <li>
        <button
          className='gx-user-profile-btn'
          type='button'
          onClick={() => {
            redirect(ROUTES.PROFILE);
          }}>
          My Account
        </button>
      </li>
      {isClientAdmin && (
        <li>
          <button
            className='gx-user-profile-btn'
            type='button'
            onClick={() => {
              redirect(ROUTES.COMPANY);
            }}>
            Company
          </button>
        </li>
      )}
      <li>
        <button className='gx-user-profile-btn' type='button' onClick={onChangePasswordClick}>
          Change Password
        </button>
      </li>
      <li>
        <button className='gx-user-profile-btn' type='button' onClick={() => dispatch(userSignOut())}>
          Log out
        </button>
      </li>
    </ul>
  );

  const LocationSelectComponent = useCallback(
    () => (
      <div>
        {locations.map((location, index) => {
          const isCurrentMainLocation = location.id === mainLocation?.id;
          return (
            <React.Fragment key={location.id}>
              <div
                onClick={() => {
                  if (!updateUserLoading) onMainLocationSelect(location);
                }}
                className={`p-2 cursor-pointer justify-between flex h-8 border border-transparent hover:border-primary-color ${
                  isCurrentMainLocation ? 'font-medium' : ''
                }`}
                style={{ transform: `translateY(${index === 0 ? 0 : -1}px)` }}>
                <div className='flex line-clamp-1'>
                  {location.name} - {formatAddress(location.address)}
                </div>
                <div className='flex items-center'>
                  {isCurrentMainLocation && !updateUserLoading && (
                    <IconComponent name='check-circle-o' color='#038fde' />
                  )}
                  {updateUserLoading && (
                    <div className='ml-2'>
                      <Spinner color='#038fde' />
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    ),
    [modalVisibleState, locations, mainLocation, updateUserLoading],
  );

  return {
    locations,
    popoverVisibility: modalVisibleState === 'popover',
    resetPasswordModalVisible: modalVisibleState === 'reset-password',
    toggle: modalVisibleState === 'upload-avatar',
    locationModalVisibility: modalVisibleState === 'location',
    onVisibleChange: onUserClick,
    onAvatarClick,
    closeUploadModal,
    onChangePasswordCancel,
    fullName,
    smallAvatarURL,
    userMenuOptions,
    avatarURL,
    mainLocation,
    LocationSelectComponent,
    onLocationClick,
    onLocationModalCancel: onLocationModalClose,
  };
};

export default useUserMenu;
